/**
 * Pure join-flow plumbing: URL parsing, storage keys, endpoints.
 * Everything here is unit-tested; main.ts only wires it to the real
 * location/localStorage.
 */

import { isValidRoomCode, normalizeRoomCode } from "@asarimitsuha/table-core";

export interface JoinParams {
  code: string | null;
  gmKey: string | null;
}

/** Parse `?room=CODE` and `#gm=KEY` (the GM half stays out of query logs). */
export function parseJoinParams(search: string, hash: string): JoinParams {
  const raw = new URLSearchParams(search).get("room");
  const code = raw !== null ? normalizeRoomCode(raw) : "";

  let gmKey: string | null = null;
  const hashBody = hash.startsWith("#") ? hash.slice(1) : hash;
  const gmRaw = new URLSearchParams(hashBody).get("gm");
  if (gmRaw !== null && gmRaw.length > 0) gmKey = gmRaw;

  return { code: isValidRoomCode(code) ? code : null, gmKey };
}

export function buildJoinPath(code: string): string {
  return `/?room=${code}`;
}

export function buildGmJoinPath(code: string, gmKey: string): string {
  return `${buildJoinPath(code)}#gm=${gmKey}`;
}

/** localStorage key holding this browser's seat id for a given room. */
export function seatStorageKey(code: string): string {
  return `vtt.seat.${code}`;
}

/** localStorage key for the seat's reclaim credential (test-spec §5.1:
 *  the id is public roster state; the secret proves entitlement). */
export function seatSecretStorageKey(code: string): string {
  return `vtt.seatSecret.${code}`;
}

/** localStorage key for the player's preferred display name. */
export const NAME_STORAGE_KEY = "vtt.playerName";

export interface EndpointEnv {
  DEV: boolean;
  VITE_SERVER_URL?: string | undefined;
}

/**
 * Websocket endpoint for the game server. Dev talks to the local server
 * port directly; production shares the client's origin (LB terminates TLS
 * and routes websockets to the server tier).
 */
export function serverEndpoint(
  loc: { protocol: string; host: string; hostname: string },
  env: EndpointEnv,
): string {
  if (env.VITE_SERVER_URL) return env.VITE_SERVER_URL;
  if (env.DEV) return `ws://${loc.hostname}:2567`;
  return `${loc.protocol === "https:" ? "wss:" : "ws:"}//${loc.host}`;
}

/**
 * HTTP base for REST and asset calls, derived from the same configured
 * endpoint as the websocket (wss://host → https://host). Empty string when
 * unconfigured — calls stay same-origin-relative, the co-hosted default.
 */
export function serverHttpBase(env: EndpointEnv): string {
  const url = env.VITE_SERVER_URL;
  if (!url) return "";
  return url.replace(/^ws(s?):/, "http$1:").replace(/\/+$/, "");
}

/** Absolutize a server-relative path against the configured backend.
 *  Absolute URLs (and anything not starting with "/") pass through. */
export function resolveServerUrl(pathOrUrl: string, env: EndpointEnv): string {
  if (!pathOrUrl.startsWith("/")) return pathOrUrl;
  return serverHttpBase(env) + pathOrUrl;
}

/** Display-boundary copy of a map with its imageUrl absolutized. The
 *  STORED url stays server-relative (portable across hosts); only what the
 *  canvas loads is resolved. */
export function resolveMapForDisplay<M extends { imageUrl: string | null }>(
  map: M,
  env: EndpointEnv,
): M {
  if (map.imageUrl === null) return map;
  return { ...map, imageUrl: resolveServerUrl(map.imageUrl, env) };
}

/**
 * fetch wrapper that routes server-relative API paths at the configured
 * backend, so a client hosted on a different origin still talks to the
 * game server for room CRUD and map upload. Non-string inputs pass through.
 */
export function serverFetch(env: EndpointEnv, fetchImpl: typeof fetch): typeof fetch {
  return ((input: RequestInfo | URL, init?: RequestInit) =>
    fetchImpl(
      typeof input === "string" ? resolveServerUrl(input, env) : input,
      init,
    )) as typeof fetch;
}
